import { AtSign } from "lucide-react";
import type { CSSProperties } from "react";
import { cn } from "@/utils/helpers";
import type { CalendarPlatform } from "@/types/contentCalendar.type";
import type { Distribution, DistributionEntry } from "@/types/productionBrief.type";

/**
 * The brief's distribution section, read-only — the per-platform copy the
 * client approves together with the creative (docs/scheduling-auto-publish-plan.md
 * §7). Shared verbatim by the operator's doc view and the client portal: both
 * sides must read the same approved copy.
 */

export const DISTRIBUTION_PLATFORM_LABEL: Record<string, string> = {
  TIKTOK: "TikTok",
  INSTAGRAM: "Instagram",
  YOUTUBE: "YouTube",
  FACEBOOK: "Facebook",
};

function HashtagRow({ tags, hue }: { tags: string[]; hue?: string }) {
  if (tags.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <AtSign className="size-3 text-co-faint" strokeWidth={2} />
      {tags.map((tag, i) => (
        <span
          key={`${tag}-${i}`}
          data-tone="quiet"
          className={cn(
            "inline-flex items-center gap-0.5 rounded-none border border-co-line bg-co-surface-solid px-2.5 py-1 text-[11px] text-co-muted",
            hue && "co-pillar-identity co-pillar-pill",
          )}
          style={hue ? ({ "--co-pillar-hue": hue } as CSSProperties) : undefined}
        >
          <span aria-hidden="true" className={cn(hue && "co-pillar-glyph")}>#</span>
          {tag.replace(/^#/, "")}
        </span>
      ))}
    </div>
  );
}

function EntryCard({
  platform,
  entry,
  pillarHue,
}: {
  platform: string;
  entry: DistributionEntry;
  pillarHue?: string;
}) {
  const isYouTube = platform === "YOUTUBE";
  const copy = isYouTube ? entry.description : entry.caption;
  const tags = isYouTube ? (entry.tags ?? []) : entry.hashtags;
  return (
    <article className="rounded-none border border-co-line bg-co-surface">
      <header className="flex items-center justify-between border-b border-co-line px-3.5 py-2.5">
        <span className="font-section text-[10.5px] font-semibold uppercase tracking-[0.16em] text-co-accent">
          {DISTRIBUTION_PLATFORM_LABEL[platform] ?? platform}
        </span>
      </header>

      {isYouTube && entry.title ? (
        <div className="border-b border-co-line p-3.5">
          <span className="tt-label uppercase text-co-faint">Title</span>
          <p dir="auto" className="mt-1.5 text-[13px] font-medium leading-snug text-co-text2">{entry.title}</p>
        </div>
      ) : null}

      <div className="p-3.5">
        <span className="tt-label uppercase text-co-faint">{isYouTube ? "Description" : "Caption"}</span>
        {/* plaintext bidi: a multilingual caption sets each paragraph's own direction. */}
        {copy ? (
          <p dir="auto" className="mt-2 whitespace-pre-wrap text-[12.5px] leading-relaxed text-co-muted [unicode-bidi:plaintext]">{copy}</p>
        ) : (
          <p className="mt-2 text-[12px] text-co-faint">Not written yet.</p>
        )}
      </div>

      {tags.length > 0 ? (
        <div className="border-t border-co-line px-3.5 py-2.5">
          <HashtagRow tags={tags} hue={pillarHue} />
        </div>
      ) : null}
    </article>
  );
}

export function DistributionSection({
  distribution,
  className,
  pillarHue,
}: {
  distribution: Distribution | undefined;
  className?: string;
  pillarHue?: string;
}) {
  const entries = Object.entries(distribution ?? {}).filter(
    ([, entry]) =>
      entry &&
      (entry.caption ||
        entry.title ||
        entry.description ||
        entry.hashtags?.length ||
        entry.tags?.length),
  );
  if (entries.length === 0) return null;

  return (
    <div className={cn("flex flex-col gap-2.5", className)}>
      <span className="font-section text-[10.5px] font-semibold uppercase tracking-[0.16em] text-co-faint">
        Copy
      </span>
      {entries.map(([platform, entry]) => (
        <EntryCard
          key={platform as CalendarPlatform}
          platform={platform}
          entry={entry}
          pillarHue={pillarHue}
        />
      ))}
    </div>
  );
}
